import { createStyles, Theme } from '@material-ui/core'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { makeStyles } from '@material-ui/core/styles'
import {
  fullsizeAbsolute,
  layoutContentMaxWidthPx,
} from '../../../../shared/style-settings'

/** Hero typography & motion — cohesive with primary brand `#1E32FA`. */
export const useMainPageInfoBlockStyles = makeStyles((theme: Theme) => {
  /** MUI spacing factors are unitless numbers; raw lengths inside strings need `px`. */
  const spacingPx = (factor: number) => `${theme.spacing(factor)}px`

  return createStyles({
    '@keyframes heroLineFade': {
      '0%': {
        opacity: 0,
        transform: `translate3d(0, ${spacingPx(1.5)}, 0)`,
      },
      '100%': { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    },
    infoBlock: {
      position: 'relative',
    },
    imageHolder: {
      position: 'relative',
      width: '100%',
      paddingLeft: `calc(${spacingPx(3)} + env(safe-area-inset-left, 0px))`,
      paddingRight: `calc(${spacingPx(3)} + env(safe-area-inset-right, 0px))`,
      paddingTop: `calc(${spacingPx(
        2.5
      )} + env(safe-area-inset-top, 0px) + clamp(2.25rem, 9vw + 0.25rem, 9.75rem))`,
      paddingBottom: `calc(${spacingPx(
        2
      )} + env(safe-area-inset-bottom, 0px) + clamp(2rem, 7vw + 0.25rem, 8rem))`,
      zIndex: 1,
      [theme.breakpoints.up('md')]: {
        paddingLeft: `calc(${spacingPx(4)} + env(safe-area-inset-left, 0px))`,
        paddingRight: `calc(${spacingPx(4)} + env(safe-area-inset-right, 0px))`,
        paddingTop: `calc(${spacingPx(
          4
        )} + env(safe-area-inset-top, 0px) + clamp(3rem, 5vw + 1.25rem, 12rem))`,
        paddingBottom: `calc(${spacingPx(
          3
        )} + env(safe-area-inset-bottom, 0px) + clamp(2.5rem, 4vw + 1rem, 10rem))`,
      },
    },
    /** Full-bleed carousel behind hero copy */
    heroCarouselRoot: {
      ...fullsizeAbsolute,
      width: '100%',
      height: '100%',
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'auto',
    },
    heroViewport: {
      height: '100%',
      overflow: 'hidden',
      width: '100%',
    },
    heroContainer: {
      display: 'flex',
      height: '100%',
      touchAction: 'pan-y pinch-zoom',
    },
    heroSlide: {
      position: 'relative',
      flex: '0 0 100%',
      minWidth: 0,
      width: '100%',
      height: '100%',
    },
    heroSlideBg: {
      ...fullsizeAbsolute,
      backgroundSize: 'cover',
      backgroundPosition: '50% 50%',
      backgroundImage: 'var(--hero-slide-bg)',
      opacity: 0,
      transition: 'opacity 360ms ease-out',
    },
    heroSlideBgVisible: {
      opacity: 1,
    },
    overflow: {
      ...fullsizeAbsolute,
      background: `linear-gradient(105deg, ${fade(
        theme.palette.common.black,
        0.82
      )} 0%, ${fade(theme.palette.common.black, 0.58)} 44%, ${fade(
        theme.palette.common.black,
        0.38
      )} 100%)`,
      zIndex: 2,
      pointerEvents: 'none',
    },
    heroVerticalSpacer: {
      height: `clamp(${spacingPx(11.25)}, 6vw + ${spacingPx(7.625)}, ${spacingPx(12.5)})`,
      flexShrink: 0,
    },
    heroImageLoader: {
      ...fullsizeAbsolute,
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.common.white,
      opacity: 1,
      transition: 'opacity 240ms ease-out',
      pointerEvents: 'none',
    },
    heroImageLoaderHidden: {
      opacity: 0,
      pointerEvents: 'none',
    },
    mainText: {
      maxWidth: layoutContentMaxWidthPx,
      margin: `${spacingPx(0)} auto`,
      zIndex: 3,
      position: 'relative',
      pointerEvents: 'none',
      '& button': {
        pointerEvents: 'auto',
      },
    },
    heroActionsRow: {
      marginBottom: theme.spacing(2),
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
    inlineTextBackdrop: {
      lineHeight: 'inherit',
      display: 'inline',
    },
    kicker: {
      pointerEvents: 'none',
      display: 'inline-block',
      fontSize: `clamp(${spacingPx(1.375)}, 1vw + 0.65rem, ${spacingPx(1.625)})`,
      fontWeight: 700,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: fade(theme.palette.common.white, 0.92),
      marginBottom: theme.spacing(1.5),
      paddingBottom: theme.spacing(0.5),
      borderBottom: `${spacingPx(0.375)} solid ${theme.palette.primary.light}`,
      textShadow: `0 ${spacingPx(0.25)} ${spacingPx(2)} ${fade(
        theme.palette.common.black,
        0.35
      )}`,
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(1.25),
        letterSpacing: '0.16em',
      },
    },
    mainTitle: {
      pointerEvents: 'none',
      fontSize: `clamp(${spacingPx(3.5)}, 10vw + 2vh, ${spacingPx(8)})`,
      maxWidth: theme.spacing(112.5),
      color: theme.palette.common.white,
      lineHeight: 1.15,
      fontWeight: 800,
      margin: 'unset',
      padding: 'unset',
      marginBottom: theme.spacing(3),
      letterSpacing: '-0.035em',
      textShadow: `0 ${spacingPx(0.25)} ${spacingPx(3)} ${fade(
        theme.palette.common.black,
        0.45
      )}, 0 ${spacingPx(1.5)} ${spacingPx(5)} ${fade(
        theme.palette.common.black,
        0.35
      )}`,
      [theme.breakpoints.down('md')]: {
        fontSize: `clamp(${spacingPx(3)}, 6vw, ${spacingPx(6)})`,
        marginBottom: theme.spacing(2.5),
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: `clamp(${spacingPx(2.5)}, 7vw, ${spacingPx(4.5)})`,
        marginBottom: theme.spacing(2),
      },
    },
    mainDesc: {
      pointerEvents: 'none',
      display: 'block',
      color: fade(theme.palette.common.white, 0.96),
      maxWidth: theme.spacing(90),
      marginBottom: theme.spacing(2.5),
      lineHeight: 1.72,
      fontSize: `clamp(${spacingPx(1.75)}, 1.2vw + 0.8vh, ${spacingPx(2.25)})`,
      fontWeight: 500,
      /** Небольшой запас высоты: меньше скачка при смене слайда, без огромной «пустой полосы» */
      minHeight: theme.spacing(6),
      [theme.breakpoints.down('md')]: {
        fontSize: `clamp(${spacingPx(1.625)}, 1.5vw, ${spacingPx(2.125)})`,
        marginBottom: theme.spacing(2),
        maxWidth: theme.spacing(81.25),
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: `clamp(${spacingPx(1.5)}, 2vw, ${spacingPx(2)})`,
        marginBottom: theme.spacing(1.75),
        maxWidth: '100%',
        minHeight: theme.spacing(5.5),
      },
    },
    heroDescLine: {
      display: 'block',
      textShadow: `0 ${spacingPx(0.125)} ${spacingPx(1.5)} ${fade(
        theme.palette.common.black,
        0.4
      )}, 0 ${spacingPx(0.5)} ${spacingPx(3)} ${fade(
        theme.palette.common.black,
        0.25
      )}`,
      animation: `$heroLineFade 0.5s cubic-bezier(0.22, 1, 0.36, 1) both`,
    },
    mainBtnHolder: {
      display: 'flex',
      gap: theme.spacing(0.5),
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      alignItems: 'center',
      minWidth: 0,
      '& .MuiButton-root': {
        flex: '0 0 auto',
        width: 'auto',
        padding: theme.spacing(1.75, 5),
        fontSize: `clamp(${spacingPx(1.75)}, 1.2vw, ${spacingPx(2.25)})`,
        minWidth: theme.spacing(17.5),
        whiteSpace: 'nowrap',
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(1.25, 3.5),
          minWidth: theme.spacing(16),
          fontSize: `clamp(${spacingPx(1.625)}, 1vw, ${spacingPx(2.125)})`,
        },
        [theme.breakpoints.down('md')]: {
          padding: theme.spacing(1.5, 4),
          minWidth: theme.spacing(18.75),
        },
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1.5, 3.5),
          fontSize: `clamp(${spacingPx(1.625)}, 1.5vw, ${spacingPx(2)})`,
          minWidth: theme.spacing(17.5),
        },
        [theme.breakpoints.down('xs')]: {
          width: '100%',
          minWidth: '100%',
          flex: '1 1 auto',
        },
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: '100%',
      },
    },
    buyButton: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
  })
})
